import { useCallback, useEffect, useState } from "react";
import { MessageCircle, MapPin, Clock, IndianRupee, Stethoscope, Star, XCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { APP_STATUS_STYLES, waLink, rupees } from "@/lib/utils-lc";
import RatingStars from "@/components/RatingStars";
import RateDialog from "@/components/RateDialog";
import CancelDialog from "@/components/CancelDialog";

export default function MyApplications() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rating, setRating] = useState(null); // { shift_id, name }
  const [cancelling, setCancelling] = useState(null); // shift_id

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/applications/mine");
      setApps(data);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="text-sm text-slate-500">Loading…</div>;
  if (apps.length === 0) return <Card className="rounded-2xl border-dashed border-slate-300 p-8 text-center text-slate-500 bg-white">You haven&apos;t applied to any shifts yet.</Card>;

  return (
    <div className="space-y-4">
      {apps.map(a => {
        const s = a.shift;
        const wa = a.status === "confirmed" || a.status === "completed" ? waLink(a.facility_phone, `Hi, I'm confirmed for the ${s?.specialty} shift on ${s?.date}.`) : null;
        const canRate = a.status === "completed" && !a.my_rating_of_facility;
        return (
          <Card key={a.application_id} className="rounded-2xl border-slate-200 p-5 bg-white" data-testid={`app-row-${a.application_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-[0.2em] text-emerald-800 flex items-center gap-1.5"><Stethoscope className="w-3 h-3" /> {s?.specialty}</div>
                <div className="font-display text-lg font-semibold mt-1">{s?.facility_name}</div>
                {a.facility_rating && <RatingStars rating={a.facility_rating} className="mt-0.5" />}
                <div className="text-sm text-slate-600 mt-1">{s?.date} · <Clock className="w-3 h-3 inline mb-0.5" /> {s?.start_time}–{s?.end_time}</div>
                <div className="text-sm text-slate-600 mt-0.5 flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {s?.location}</div>
              </div>
              <div className="text-right">
                <Badge className={APP_STATUS_STYLES[a.status] + " hover:" + APP_STATUS_STYLES[a.status]}>{a.status}</Badge>
                <div className="mt-2 font-display font-bold flex items-center justify-end"><IndianRupee className="w-4 h-4" />{Number(s?.pay_inr || 0).toLocaleString("en-IN")}</div>
              </div>
            </div>

            {a.payment && (a.status === "confirmed" || a.status === "completed") && (
              <div className="mt-3 border-t border-slate-100 pt-3 text-xs text-slate-500 flex flex-wrap gap-x-4 gap-y-1">
                <span>Payment: <b className="text-slate-700">{a.payment.method === "mock_escrow" ? "Escrow" : a.payment.method.toUpperCase()}</b></span>
                <span>Status: <b className="text-slate-700">{a.payment.status}</b></span>
                <span>Your net: <b className="text-slate-700">{rupees(a.payment.net_inr)}</b> (after 10% fee)</span>
              </div>
            )}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {wa && (
                <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full bg-[#25D366] hover:bg-[#20BD5A] text-white text-sm font-medium px-4 py-2" data-testid={`wa-link-${a.application_id}`}>
                  <MessageCircle className="w-4 h-4" /> WhatsApp facility
                </a>
              )}
              {canRate && (
                <Button size="sm" onClick={() => setRating({ shift_id: a.shift_id, name: s?.facility_name })} className="rounded-full bg-amber-500 hover:bg-amber-600" data-testid={`rate-btn-${a.application_id}`}>
                  <Star className="w-3.5 h-3.5 mr-1" /> Rate facility
                </Button>
              )}
              {a.status === "confirmed" && (
                <Button size="sm" variant="outline" onClick={() => setCancelling(a.shift_id)} className="rounded-full border-rose-300 text-rose-700 hover:bg-rose-50" data-testid={`pro-cancel-${a.application_id}`}>
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Cancel
                </Button>
              )}
              {a.my_rating_of_facility && (
                <span className="inline-flex items-center gap-1 text-xs text-slate-500"><Star className="w-3 h-3 fill-amber-400 text-amber-400" /> You rated {a.my_rating_of_facility.stars}★</span>
              )}
            </div>
          </Card>
        );
      })}
      <RateDialog
        open={!!rating}
        onClose={() => setRating(null)}
        shiftId={rating?.shift_id}
        rateeName={rating?.name}
        onDone={async () => { setRating(null); await load(); }}
      />
      <CancelDialog
        open={!!cancelling}
        onClose={() => setCancelling(null)}
        shiftId={cancelling}
        onDone={async () => { setCancelling(null); await load(); }}
      />
    </div>
  );
}
